import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';
import { Button, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { filter, find, get, head, isEmpty, isNil, keys, last, map, toString } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';
import shp from 'shpjs';
import * as topojson from 'topojson-client';
import * as atlasOps from '../../../api/Atlas/atlas-operations';
import { FILE_TYPES } from '../../../api/Connectors/Connectors';
import * as taskOps from '../../../api/Tasks/task-operations';
import { STATUS_TYPES } from '../../../api/Tasks/taskConstants';
import TelemetryConnections from '../../../api/TelemetryConnections/TelemetryConnections';

import { readUploadedFileAsText } from '../../../modules/export-import-files';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';
import ogr2ogrTask from '../../../modules/tasks/ogr2ogrTask';

import Loading from '../../components/Loading';
import Validation from '../../containers/Validation';

const GDAL_INFO_URL = 'https://gdal.org/programs/ogr2ogr.html';

class Ogr2ogrConfiguration extends Component {
  updateInterval = null;

  state = {
    disabled: true,
    fileName: '',
    geometry: {},
    isTaskRunning: false,
    showAdvancedOptions: false,
    taskId: ''
  };

  componentWillUnmount() {
    clearInterval(this.updateInterval);
  }

  changeShowAdvancedOptions = event => this.setState({ showAdvancedOptions: event.target.checked });

  downloadResultFile = event => {
    const { fileName } = this.state;
    const outputFileName = head(fileName.split('.'));
    const fileType = event.target.form.fileType.value.trim();
    const { extension } = find(FILE_TYPES.properties, ['value', fileType]) || {};
    if (!isNil(extension))
      window.open(
        `https://video.ipsilum.com/demos_user/public/ogr2ogrAtlas/${outputFileName}.${extension}`,
        `_blank`
      );
  };

  getTaskStatus = async () => {
    const { atlas, gmsConnection } = this.props;
    const { taskId } = this.state;
    try {
      const getTaskMonitorParams = {
        debugIP: isEmpty(gmsConnection) ? '' : atlasOps.getGMSDirection(gmsConnection),
        authenticate: false,
        isTasker: false,
        itemForOwner: { id: atlasOps.getId(atlas), collectionName: 'atlas' }
      };

      const validatedTasks = await methodPromise('tasks.getTaskMonitor', getTaskMonitorParams);
      const task = find(validatedTasks, ['_id', taskId]) || {};

      if (taskOps.getStatus(task) === STATUS_TYPES.ERROR) throw new Error('Server error');

      if (taskOps.getStatus(task) === STATUS_TYPES.DONE) {
        clearInterval(this.updateInterval);
        this.setState({ disabled: false, isTaskRunning: false });
      }
    } catch (error) {
      handleErrorInClient(error);
      clearInterval(this.updateInterval);
      this.setState({ isTaskRunning: false });
    }
  };

  handleSubmit = async form => {
    try {
      const { atlas, gmsConnection, isPublic } = this.props;
      const { geometry, fileName } = this.state;

      const name = form.name.value.trim();
      const parameters = get(form, 'parameters.value', '').trim();
      const fileType = form.fileType.value.trim();
      const { extension } = find(FILE_TYPES.properties, ['value', fileType]) || {};

      const existingTasks = await methodPromise('tasks.checkAtlasTaskName', {
        atlas,
        isPublic,
        label: taskOps.getLabel(ogr2ogrTask({})),
        name
      });
      const nameForTask = `${name} ${existingTasks > 0 ? toString(existingTasks) : ''}`;
      const taskToRun = ogr2ogrTask({
        extension,
        geometry,
        fileName: head(fileName.split('.')),
        fileType,
        owner: atlasOps.getOwner(atlas),
        parameters,
        taskName: nameForTask.trim()
      });
      const taskId = await methodPromise('tasks.runTask', {
        authenticate: false,
        debugIP: isEmpty(gmsConnection) ? '' : atlasOps.getGMSDirection(gmsConnection),
        isTasker: false,
        itemForOwner: { id: atlasOps.getId(atlas), collectionName: 'atlas' },
        taskToRun
      });

      Bert.alert(`Task launched with alias ${nameForTask}`, 'info');
      this.setState({ taskId, isTaskRunning: true });
      this.updateInterval = setInterval(this.getTaskStatus, 500);
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  loadInputFile = async event => {
    let geometryFile = {};
    let key = '';
    let arrayBuffer = {};

    const file = event.target.files[0];
    try {
      const fileInput = event.target;
      const stringResult = await readUploadedFileAsText(file);

      if (!isNil(file)) this.setState({ fileName: file.name });

      const extension = last(file.name.split('.'));
      switch (extension) {
        case 'geojson':
          geometryFile = JSON.parse(stringResult);
          break;
        case 'topoJSON':
          geometryFile = JSON.parse(stringResult);
          [key] = keys(geometryFile.objects);
          geometryFile = topojson.feature(geometryFile, geometryFile.objects[key]);
          break;
        case 'zip':
          arrayBuffer = await file.arrayBuffer();
          geometryFile = await shp(arrayBuffer);
          break;
        default:
          throw new Error('File extension is not supported');
      }
      Bert.alert('File loaded', 'success');
      fileInput.value = '';
      this.setState({ geometry: JSON.stringify(geometryFile) });
    } catch (exception) {
      this.setState({ fileName: '' });
      handleErrorInClient(exception);
    }
  };

  render() {
    const { isPreview, loading } = this.props;
    const { disabled, fileName, isTaskRunning, showAdvancedOptions } = this.state;

    const validationProps = {
      rules: {
        name: {
          required: true
        },
        fileType: {
          required: true
        }
      },
      messages: {
        name: {
          required: 'This needs an alias.'
        },
        fileType: {
          required: 'Select an output extension.'
        }
      },
      submitHandler: form => this.handleSubmit(form)
    };

    if (loading) return <Loading />;

    return (
      <Validation {...validationProps}>
        <Form
          style={{ height: '100%', overflow: 'hidden scroll' }}
          onSubmit={event => event.preventDefault()}
        >
          <Row className="m-1">
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="d-flex bd-highlight">Task Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  defaultValue=""
                  placeholder="What's the task alias?"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label className="d-flex bd-highlight">Input File</Form.Label>
                <InputGroup>
                  <Form.Label
                    className="d-flex bd-highlight custom-file-label col-12"
                    htmlFor="fileName"
                  >
                    {!isEmpty(fileName) ? fileName : 'Upload File'}
                  </Form.Label>
                  <Form.Control
                    type="file"
                    name="fileName"
                    className="custom-file-input"
                    id="fileName"
                    aria-describedby="inputFile"
                    onChange={this.loadInputFile}
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group>
                <Form.Label className="d-flex bd-highlight">Output extension</Form.Label>
                <Form.Control as="select" name="fileType" id="fileType">
                  <option value="">-</option>
                  {map(
                    filter(FILE_TYPES.properties, ({ extension }) => !isNil(extension)),
                    ({ name, value, humanValue }) => (
                      <option key={name} value={value}>
                        {humanValue}
                      </option>
                    )
                  )}
                </Form.Control>
              </Form.Group>
              <Form.Row>
                <Form.Check
                  type="switch"
                  id="advanced-options"
                  label="Advanced Options"
                  checked={showAdvancedOptions}
                  onChange={this.changeShowAdvancedOptions}
                />
              </Form.Row>
              {showAdvancedOptions && (
                <Form.Group>
                  <Form.Label className="d-flex bd-highlight">Parameters</Form.Label>
                  <Form.Control
                    as="textarea"
                    type="text"
                    name="parameters"
                    defaultValue=""
                    placeholder="Ogr2ogr params in ssv format"
                  />
                  <Form.Text className="text-justify text-muted text-break">
                    Further info:{' '}
                    <a href={GDAL_INFO_URL} target="_blank" rel="noreferrer noopener">
                      {GDAL_INFO_URL}
                    </a>
                  </Form.Text>
                </Form.Group>
              )}
              <Form.Row className="d-flex bd-highlight mx-0">
                <Button type="submit" variant="success" disabled={isPreview || isTaskRunning}>
                  Run task
                </Button>
                {isTaskRunning ? (
                  <Loading />
                ) : (
                  <Button
                    variant="secondary"
                    disabled={disabled || isPreview}
                    onClick={this.downloadResultFile}
                  >
                    Download results
                  </Button>
                )}
              </Form.Row>
            </Col>
          </Row>
        </Form>
      </Validation>
    );
  }
}

Ogr2ogrConfiguration.defaultProps = {
  atlas: {},
  gmsConnection: {},
  isPreview: false,
  isPublic: false
};

Ogr2ogrConfiguration.propTypes = {
  atlas: PropTypes.object,
  gmsConnection: PropTypes.object,
  isPreview: PropTypes.bool,
  isPublic: PropTypes.bool,
  loading: PropTypes.bool.isRequired
};

export default withTracker(({ atlas }) => {
  let gmsConnectionId = '';
  let subscription = {};
  if (!isNil(atlas)) {
    gmsConnectionId = atlasOps.getGMSConnection(atlas);
    subscription = Meteor.subscribe('gmsConnections.public', gmsConnectionId);
  }
  return {
    loading: !isNil(atlas) && !subscription.ready(),
    gmsConnection: TelemetryConnections.findOne(gmsConnectionId)
  };
})(Ogr2ogrConfiguration);
